import type {
	CompositeFilterDescriptor,
	FilterDescriptor,
} from "@tql/ui-components-react/kendo/data-query";

const MIN_STRING_FILTER_LENGTH = 3;

const isCompositeFilterDescriptor = (
	descriptor: FilterDescriptor | CompositeFilterDescriptor,
): descriptor is CompositeFilterDescriptor => {
	return "filters" in descriptor && "logic" in descriptor;
};

const formatValue = (value: unknown): string | null => {
	if (value === null || value === undefined) return null;
	if (typeof value === "string") {
		const trimmed = value.trim();
		if (trimmed.length < MIN_STRING_FILTER_LENGTH) return null;
		return trimmed;
	}
	if (typeof value === "number") {
		return Number.isFinite(value) ? String(value) : null;
	}
	if (typeof value === "boolean") {
		return String(value);
	}
	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? null : value.toISOString();
	}
	return null;
};

/**
 * Walks a Kendo `CompositeFilterDescriptor` tree and returns the set of
 * field names that have any non-empty value, regardless of whether
 * {@link extractColumnFilters} would forward them to the backend. Useful
 * for detecting the "interim" typing state where a string filter is
 * present but below {@link MIN_STRING_FILTER_LENGTH}.
 */
export const collectActiveFilterFields = (
	filter: CompositeFilterDescriptor | null | undefined,
): Set<string> => {
	const fields = new Set<string>();
	if (!filter) return fields;

	const visit = (
		descriptor: FilterDescriptor | CompositeFilterDescriptor,
	): void => {
		if (isCompositeFilterDescriptor(descriptor)) {
			for (const child of descriptor.filters) {
				visit(child);
			}
			return;
		}
		if (typeof descriptor.field !== "string") return;
		const value: unknown = descriptor.value;
		if (value === null || value === undefined) return;
		if (typeof value === "string" && value.trim().length === 0) return;
		fields.add(descriptor.field);
	};

	visit(filter);
	return fields;
};

/**
 * Walks a Kendo `CompositeFilterDescriptor` tree and returns a map of
 * `field` -> stringified value for filters that should trigger a backend
 * fetch. String filters are only included once the user has typed at
 * least {@link MIN_STRING_FILTER_LENGTH} characters. Non-string filters
 * (numbers, booleans, dates) are always included.
 *
 * When the same field appears multiple times, the last value wins.
 */
export const extractColumnFilters = (
	filter: CompositeFilterDescriptor | null | undefined,
): Record<string, string> => {
	const result: Record<string, string> = {};
	if (!filter) return result;

	const visit = (
		descriptor: FilterDescriptor | CompositeFilterDescriptor,
	): void => {
		if (isCompositeFilterDescriptor(descriptor)) {
			for (const child of descriptor.filters) {
				visit(child);
			}
			return;
		}
		const field =
			typeof descriptor.field === "string" ? descriptor.field : null;
		if (!field) return;
		const formatted = formatValue(descriptor.value);
		if (formatted === null) return;
		result[field] = formatted;
	};

	visit(filter);
	return result;
};
